import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Eye, Download, Pencil, Trash2, CheckCircle, Receipt } from 'lucide-react';

const INVOICE_STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  overdue: 'bg-red-100 text-red-700',
};

export function InvoicesTable({ invoices, isAdmin, isStaff, canCreate, getClientName, getFirmName, fmtCurrency, onView, onDownloadPdf, onEdit, onDelete, onStatusChange }) {
  if (invoices.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Receipt className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-semibold mb-1">No invoices</h3>
          <p className="text-sm text-muted-foreground">Create your first invoice to get started.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <ScrollArea className="w-full">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">Invoice #</TableHead>
              <TableHead>Client</TableHead>
              {isAdmin && <TableHead>Firm</TableHead>}
              <TableHead>Description</TableHead>
              <TableHead className="whitespace-nowrap">Invoice Date</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              {isAdmin && <TableHead className="text-right">CGST</TableHead>}
              {isAdmin && <TableHead className="text-right">SGST</TableHead>}
              {isAdmin && <TableHead className="text-right">IGST</TableHead>}
              <TableHead className="text-right whitespace-nowrap">Invoice Value</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="whitespace-nowrap">Due Date</TableHead>
              {canCreate && <TableHead>Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map(inv => (
              <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                <TableCell className="font-medium text-sm whitespace-nowrap">{inv.invoice_number}</TableCell>
                <TableCell className="text-sm">{getClientName(inv.client_id)}</TableCell>
                {isAdmin && <TableCell className="text-xs text-muted-foreground">{getFirmName(inv.firm_id) || '-'}</TableCell>}
                <TableCell className="text-sm text-muted-foreground max-w-[180px] truncate">{inv.service_description || '-'}</TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{inv.invoice_date || '-'}</TableCell>
                <TableCell className="text-sm text-right whitespace-nowrap">Rs. {fmtCurrency(inv.amount)}</TableCell>
                {isAdmin && <TableCell className="text-xs text-right whitespace-nowrap">{inv.cgst_amount != null ? `Rs. ${fmtCurrency(inv.cgst_amount)}` : '-'}</TableCell>}
                {isAdmin && <TableCell className="text-xs text-right whitespace-nowrap">{inv.sgst_amount != null ? `Rs. ${fmtCurrency(inv.sgst_amount)}` : '-'}</TableCell>}
                {isAdmin && <TableCell className="text-xs text-right whitespace-nowrap">{inv.igst_amount != null ? `Rs. ${fmtCurrency(inv.igst_amount)}` : '-'}</TableCell>}
                <TableCell className="text-sm font-semibold text-right whitespace-nowrap text-teal-700">Rs. {fmtCurrency(inv.invoice_value ?? inv.amount)}</TableCell>
                <TableCell><Badge className={`text-xs ${INVOICE_STATUS_COLORS[inv.status]}`} data-testid="invoice-status-badge">{inv.status}</Badge></TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{inv.due_date || '-'}</TableCell>
                {canCreate && (
                  <TableCell>
                    {isAdmin && (
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onView(inv)} title="View" data-testid={`invoice-view-${inv.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onDownloadPdf(inv)} title="Download PDF" data-testid={`invoice-pdf-${inv.id}`}><Download className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onEdit(inv)} title="Edit" data-testid={`invoice-edit-${inv.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => onDelete(inv)} title="Delete" data-testid={`invoice-delete-${inv.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        <Select value={inv.status} onValueChange={v => onStatusChange(inv.id, v)}>
                          <SelectTrigger className="w-24 h-7 text-xs" data-testid={`invoice-status-select-${inv.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{['draft', 'sent', 'paid', 'overdue'].map(s => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    )}
                    {isStaff && !isAdmin && inv.status !== 'paid' && (
                      <Button size="sm" variant="outline" className="h-7 text-xs text-green-700 border-green-200 hover:bg-green-50" onClick={() => onStatusChange(inv.id, 'paid')} data-testid={`invoice-mark-paid-${inv.id}`}>
                        <CheckCircle className="h-3.5 w-3.5 mr-1" /> Mark Paid
                      </Button>
                    )}
                    {isStaff && !isAdmin && inv.status === 'paid' && (
                      <Badge className="text-xs bg-green-100 text-green-700">Paid</Badge>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </Card>
  );
}
